import { useState, useEffect } from "react";
import FormInput from "./FormInput";
import QualificationDropdown from "./QualificationDropdown";
import RankDropdown from "./RankDropdown";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import app from "../../firebase/firebase";

const db = getFirestore(app);

export default function MockSSCESupervisorForm() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false); // 🔵 NEW: Loading state
  const [form, setForm] = useState(
    JSON.parse(localStorage.getItem("mockSSCESupervisorForm")) || {
      school: "",
      principal: "",
      name: "",
      qualification: "",
      experience: "",
      rank: "",
      centre: "",
    }
  );
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Auto-save to localStorage
  useEffect(() => {
    localStorage.setItem("mockSSCESupervisorForm", JSON.stringify(form));
  }, [form]);

  const update = (field, value) => setForm({ ...form, [field]: value });

  const validateStep = () => {
    let currentErrors = {};
    if (step === 1) {
      if (!form.school.trim()) currentErrors.school = "School name is required";
      if (!form.principal.trim())
        currentErrors.principal = "Principal's name is required";
    }
    if (step === 2) {
      if (!form.name.trim()) currentErrors.name = "Supervisor name is required";
      if (!form.qualification.trim())
        currentErrors.qualification = "Qualification is required";
      if (!form.experience || form.experience < 0)
        currentErrors.experience = "Experience must be 0 or more";
      if (!form.rank.trim()) currentErrors.rank = "Grade Level is required";
      if (!form.centre.trim())
        currentErrors.centre = "Custodian centre is required";
    }
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const nextStep = () => validateStep() && setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true); // 🔵 Start loading

    try {
      const collectionRef = collection(db, "mockSSCESupervisors");
      await addDoc(collectionRef, {
        ...form,
        submittedAt: serverTimestamp(),
      });

      localStorage.removeItem("mockSSCESupervisorForm");
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting MOCK SSCE Supervisor Form:", error);
      alert("Failed to submit form. Please try again.");
    }

    setLoading(false); // 🔵 Stop loading
  };

  if (submitted)
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white shadow-xl rounded-2xl text-center">
        <h2 className="text-3xl font-bold text-green-700 mb-4">
          Form Submitted Successfully!
        </h2>
        <p className="text-gray-600 mb-6">
          Thank you for submitting the MOCK SSCE Supervisor nomination.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 transition"
        >
          Submit Another Form
        </button>
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto p-8 bg-white shadow-xl rounded-2xl">
      {/* 🔙 BACK BUTTON */}
      <button
        onClick={() => window.history.back()}
        className="mb-6 text-sm text-green-700 hover:text-green-900 transition flex items-center"
      >
        ← Back
      </button>

      <h2 className="text-3xl font-bold mb-2 text-center text-green-800">
        MOCK SSCE – Supervisor Nomination
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Fill out your school and supervisor details step by step.
      </p>

      {/* Progress bar */}
      <div className="flex mb-8">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`flex-1 h-2 mx-1 rounded-full ${
              step >= s ? "bg-green-600" : "bg-gray-300"
            }`}
          />
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="space-y-4">
            <FormInput
              label="Name of School"
              value={form.school}
              onChange={(e) => update("school", e.target.value)}
              required
              error={errors.school}
            />
            <FormInput
              label="Principal's Name"
              value={form.principal}
              onChange={(e) => update("principal", e.target.value)}
              required
              error={errors.principal}
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <FormInput
              label="Supervisor Name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              error={errors.name}
            />

            <QualificationDropdown
              value={form.qualification}
              onChange={(val) => update("qualification", val)}
              required
              error={errors.qualification}
            />

            <FormInput
              type="number"
              label="Supervision Experience (years)"
              value={form.experience}
              onChange={(e) => update("experience", e.target.value)}
              required
              error={errors.experience}
            />

            <RankDropdown
              value={form.rank}
              onChange={(val) => update("rank", val)}
              required
              error={errors.rank}
            />

            <FormInput
              label="Custodian Centre"
              value={form.centre}
              onChange={(e) => update("centre", e.target.value)}
              required
              error={errors.centre}
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-4 text-green-700">
              Review & Submit
            </h3>
            <p><strong>School:</strong> {form.school}</p>
            <p><strong>Principal's Name:</strong> {form.principal}</p>
            <p><strong>Supervisor Name:</strong> {form.name}</p>
            <p><strong>Qualification:</strong> {form.qualification}</p>
            <p><strong>Experience:</strong> {form.experience} years</p>
            <p><strong>Grade Level:</strong> {form.rank}</p>
            <p><strong>Custodian Centre:</strong> {form.centre}</p>
          </div>
        )}

        <div className="flex justify-between mt-6">
          {step > 1 && (
            <button
              type="button"
              onClick={prevStep}
              disabled={loading}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition"
            >
              Previous
            </button>
          )}

          {step < 3 && (
            <button
              type="button"
              onClick={nextStep}
              disabled={loading}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
            >
              Next
            </button>
          )}

          {step === 3 && (
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 rounded-md text-white ${
                loading
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800"
              } transition flex items-center gap-2`}
            >
              {loading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {loading ? "Submitting..." : "Submit Form"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
